import { sortAsc } from '@strata/helper-functions';
import { validationStrings } from '@strata/strata-ui-strings';
import { getIn } from 'formik';
import * as Yup from 'yup';

export const Sections = {
    AUTH_DETAILS: 'authDetails',
    PATIENT: 'patient',
    PRODUCT: 'product',
};

export const StepToSectionMap = {
    1: Sections.PATIENT,
    2: Sections.PRODUCT,
    3: Sections.AUTH_DETAILS,
};

export const SectionToStepMap = {
    [Sections.PATIENT]: 1,
    [Sections.PRODUCT]: 2,
    [Sections.AUTH_DETAILS]: 3,
};

export const selectOptionSort = ({ name: a }, { name: b }) => sortAsc(a, b);

export const AuthFlags = {
    TOC_FLAG: 'Transition of care',
    WAIT_FOR_CLAIM: 'Wait for claim',
    GRANDFATHERED: 'Grandfathered',
};

export const PayerReferralStatuses = ['APPROVED', 'CANCEL', 'BENE_DENY', 'DENIED'];

const required = validationStrings.requiredField.errorMessage;
export const validationSchema = Yup.object({
    [Sections.PATIENT]: Yup.object({
        healthPlan: Yup.object().required(required),
        patientFirstName: Yup.string().required(required),
        patientLastName: Yup.string().required(required),
        cardholderId: Yup.string().required(required),
        memberDOB: Yup.date().typeError(required).required(required),
        eligibility: Yup.object({
            is_eligible: Yup.bool(),
            eligibilityConfirmed: Yup.bool().oneOf([true]).required(required),
            benefit_group: Yup.string(),
            member_zip: Yup.string(),
        }).required(),
        memberBenefitCode: Yup.string(),
    }),
    [Sections.PRODUCT]: Yup.object({
        benefit: Yup.object().required(required),
        productOid: Yup.object().required(required),
        productService: Yup.object().required(required),
        productDays: Yup.number()
            .positive(validationStrings.number.positiveNumbersOnly)
            .required(required),
        productQty: Yup.number()
            .positive(validationStrings.number.positiveNumbersOnly)
            .required(required),
        strength: Yup.string(),
        rxRefillCount: Yup.number().positive(validationStrings.number.positiveNumbersOnly),
        rxSig: Yup.string(),
        offerRate: Yup.number()
            .positive(validationStrings.number.positiveNumbersOnly)
            .lessThan(1, 'Must be less than 1.'),
    }).required(),
    [Sections.AUTH_DETAILS]: Yup.object({
        caseId: Yup.string().required(required),
        authExpiration: Yup.date().required(required),
        prescriber: Yup.string()
            .length(10, validationStrings.exactCharacter.lambda(10))
            .required(required),
        placeOfService: Yup.object().required(required),
        diagnosisCode: Yup.string().required(required),
        authReason: Yup.string(),
        payerReferralStatus: Yup.string(),
        requestStage: Yup.string(),
        serviceStartDate: Yup.date(),
        specialInstructions: Yup.string(),
        billingProvider: Yup.string().length(10, validationStrings.exactCharacter.lambda(10)),
        prescriberPracticeNpi: Yup.string().length(10, validationStrings.exactCharacter.lambda(10)),
        requestingProvider: Yup.string().length(10, validationStrings.exactCharacter.lambda(10)),
        servicingFacility: Yup.string().length(10, validationStrings.exactCharacter.lambda(10)),
        servicingProvider: Yup.string().length(10, validationStrings.exactCharacter.lambda(10)),
        pharmacy: Yup.object(),
        flags: Yup.array(),
    }).required(),
});

export const formatPriorAuthBody = (values) => {
    const {
        healthPlan,
        cardholderId,
        patientFirstName,
        patientLastName,
        memberDOB,
        memberBenefitCode,
        eligibility,
    } = getIn(values, Sections.PATIENT);
    const {
        benefit,
        productService,
        strength,
        productDays,
        productQty,
        rxRefillCount,
        rxSig,
        offerRate,
    } = getIn(values, Sections.PRODUCT);
    const {
        caseId,
        authExpiration,
        prescriber,
        placeOfService,
        authReason,
        diagnosisCode,
        payerReferralStatus,
        requestStage,
        serviceStartDate,
        specialInstructions,
        billingProvider,
        prescriberPracticeNpi,
        requestingProvider,
        servicingFacility,
        servicingProvider,
        pharmacy,
        flags,
    } = getIn(values, Sections.AUTH_DETAILS);

    return {
        // just auth
        case_id: caseId, // Type: str
        auth_expiration: authExpiration, // Type: date
        auth_reason: authReason, // Type: str | None; Default: None
        billing: benefit.value, // Type: str
        diagnosis_code: diagnosisCode, // Type: str | None; Default: None
        payer_referral_status: payerReferralStatus, // Type: PayerReferralStatus; Default: PayerReferralStatus.APPROVED
        place_of_service: placeOfService?.secondaryData, // Type: str | None; Default: None
        request_stage: requestStage, // Type: str | None; Default: None
        segment_id: healthPlan.secondaryData, // Type: str
        service_start_date: serviceStartDate, // Type: date | None; Default: None
        special_instructions: specialInstructions, // Type: str | None; Default: None
        // Providers
        billing_provider: billingProvider, // Type: str | None; Default: None
        prescriber_npi: prescriber, // Type: str | None
        prescriber_practice_npi: prescriberPracticeNpi, // Type: str | None; Default: None
        requesting_provider: requestingProvider, // Type: str | None; Default: None
        servicing_facility: servicingFacility, // Type: str | None; Default: None
        servicing_provider: servicingProvider, // Type: str | None; Default: None
        supplier_npi: pharmacy?.secondaryData, // Type: str | None; Default: None
        // Flags
        toc_flag: (flags ?? []).includes(AuthFlags.TOC_FLAG), // Type: bool; Default: False
        wait_for_claim: (flags ?? []).includes(AuthFlags.WAIT_FOR_CLAIM), // Type: bool; Default: False
        grandfathered: (flags ?? []).includes(AuthFlags.GRANDFATHERED), // Type: bool; Default: False
        // just member
        member_id: cardholderId, // Type: str
        member_benefit_code: memberBenefitCode, // Type: str | None; Default: None
        member_benefit_group: eligibility.member_benefit_group?.trim(), // Type: str | None; Default: None
        patient_dob: memberDOB, // Type: date
        patient_first_name: patientFirstName, // Type: str
        patient_last_name: patientLastName, // Type: str
        // just product
        product_service_id: productService.code, // Type: str
        is_brand: productService.is_brand, // Type: bool | None; Default: None
        offer_rate: offerRate, // Type: Decimal | None; Default: None
        product_days: productDays, // Type: int | None; Default: None
        product_qty: productQty, // Type: Decimal | None; Default: None
        rx_refillcount: rxRefillCount || undefined, // Type: int; Default: 0
        rx_sig: rxSig, // Type: str | None; Default: None
        strength, // Type: str | None; Default: None
    };
};
